import axios from 'axios';
import config from '../config.js';

// Sign-in goes through Infor OS (OAuth2 implicit grant via the ION API authorization server).
// The bearer token comes back on the /callback URL and is sent on every ION API request.

const authTokenKey = 'autkv8';
const tokenExpiryKey = 'autexp';
const configTokenKey = 'conf';
const usernameKey = 'authuser';
const oauthStateKey = 'oauthstate';
const signOutReasonKey = 'signoutreason';

function storedToken() {
    let token = localStorage.getItem(authTokenKey);
    let expiry = parseInt(localStorage.getItem(tokenExpiryKey), 10);

    if (!token || !expiry || Date.now() >= expiry) {
        return null;
    }
    return token;
}

// Pull the user's name out of the IFS users/me reply, wherever it sits in the JSON
function findUserName(data) {
    const preferredKeys = ['userName', 'UserName', 'username', 'displayName', 'DisplayName', 'email', 'Email'];
    let found = {};

    (function walk(node) {
        if (!node || typeof node !== 'object') {
            return;
        }
        Object.keys(node).forEach((key) => {
            let value = node[key];
            if (typeof value === 'string' && value && preferredKeys.includes(key) && !(key in found)) {
                found[key] = value;
            }
            else if (typeof value === 'object') {
                walk(value);
            }
        });
    })(data);

    let key = preferredKeys.find((k) => k in found);
    return key ? found[key] : null;
}

function randomState() {
    let bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function applyHeaders(token, configuration) {
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    if (configuration) {
        axios.defaults.headers.common['X-Infor-MongooseConfig'] = configuration;
    }
}

applyHeaders(storedToken(), localStorage.getItem(configTokenKey));

const AuthService = {
    session: storedToken(),
    configuration: localStorage.getItem(configTokenKey),

    isLoggedIn() {
        return storedToken() !== null;
    },

    // Step 1: send the user to the Infor sign-in page
    login(configuration) {
        this.setSytelineConfig(configuration);

        let state = randomState();
        sessionStorage.setItem(oauthStateKey, state);

        let params = new URLSearchParams({
            client_id: config.ionApi.clientId,
            response_type: 'token',
            redirect_uri: config.redirectUrl,
            state: state,
        });

        window.location.assign(`${config.ionApi.authServerUrl}${config.ionApi.authorizeEndpoint}?${params.toString()}`);
    },

    // Step 2: on /callback, read the token from the URL fragment
    completeLogin() {
        return new Promise((resolve, reject) => {
            let params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
            let expectedState = sessionStorage.getItem(oauthStateKey);
            sessionStorage.removeItem(oauthStateKey);

            if (params.get('error')) {
                reject(`Sign-in failed: ${params.get('error_description') || params.get('error')}`);
                return;
            }

            let token = params.get('access_token');
            if (!token) {
                reject('Sign-in failed: no access token was returned');
                return;
            }

            if (!expectedState || params.get('state') !== expectedState) {
                reject('Sign-in failed: state mismatch, please try again');
                return;
            }

            let expiresIn = parseInt(params.get('expires_in'), 10) || 7200;
            this.setSession(token, expiresIn);

            // Confirm the token works against SyteLine, the same check the on-prem login did
            axios.get(`${config.restBaseUrl}SLProjs/ProjNum`)
                .then((res) => {
                    if (res.data && res.data.MessageCode >= 300) {
                        this.logout();
                        reject(`Signed in, but SyteLine rejected the request: ${res.data.Message || res.data.MessageCode}`);
                        return;
                    }
                    return this.loadUsername().then(() => resolve());
                })
                .catch((err) => {
                    this.logout();
                    // err is either an axios error (HTTP failure) or a response the interceptor rejected (MessageCode >= 300)
                    let detail = (err && err.data && (err.data.Message || err.data.MessageCode))
                        || (err && err.response && `HTTP ${err.response.status} ${JSON.stringify(err.response.data)}`)
                        || (err && err.message)
                        || String(err);
                    reject(`Signed in, but the SyteLine check call failed: ${detail}`);
                });
        });
    },

    // The dashboard records who last saved a project (storageService). The token doesn't carry
    // the user name, and SyteLine Cloud doesn't allow dbo.UserNameSp() in an IDO filter, so ask
    // Infor OS user management (IFS) who is signed in. Failing here doesn't block the login.
    loadUsername() {
        return axios.get(`${config.ionApi.gatewayUrl}/${config.ionApi.tenant}/ifsservice/usermgt/v2/users/me`, {
            headers: { 'Accept': 'application/json' },
        })
            .then((res) => {
                let name = findUserName(res.data);
                if (name) {
                    console.info('Signed in as ' + name);
                    this.setUsername(name);
                }
                else {
                    console.warn('Could not determine user name: ' + JSON.stringify(res.data));
                }
            })
            .catch((err) => console.warn('Could not determine user name: '
                + (err && err.data ? JSON.stringify(err.data)
                    : err && err.response ? `HTTP ${err.response.status} ${JSON.stringify(err.response.data)}`
                    : (err && err.message) || String(err))));
    },

    setSession(token, expiresInSeconds) {
        // Treat the token as expired a minute early so requests don't fail mid-flight
        let expiry = Date.now() + Math.max(expiresInSeconds - 60, 0) * 1000;
        localStorage.setItem(authTokenKey, token);
        localStorage.setItem(tokenExpiryKey, String(expiry));
        this.session = token;
        applyHeaders(token, this.configuration);
    },

    setSytelineConfig(configuration) {
        localStorage.setItem(configTokenKey, configuration);
        this.configuration = configuration;
        applyHeaders(null, configuration);
    },

    getSytelineConfig() {
        return localStorage.getItem(configTokenKey);
    },

    isFAMConfig() {
        let sytelineConfig = localStorage.getItem(configTokenKey);
        if (!sytelineConfig) {
            return false;
        }
        // Cloud names are <tenant>_FAM; on-prem it was just FAM
        sytelineConfig = sytelineConfig.toUpperCase();
        return sytelineConfig === 'FAM' || sytelineConfig.endsWith('_FAM');
    },

    getToken() {
        return storedToken();
    },

    setUsername(username) {
        localStorage.setItem(usernameKey, username);
    },

    getUsername() {
        return localStorage.getItem(usernameKey);
    },

    // Why the app last sent the user back to /login (shown once on the login page)
    setSignOutReason(reason) {
        console.error('Signed out:', reason);
        sessionStorage.setItem(signOutReasonKey, reason);
    },

    takeSignOutReason() {
        let reason = sessionStorage.getItem(signOutReasonKey);
        sessionStorage.removeItem(signOutReasonKey);
        return reason;
    },

    // True when a token was saved but has run out
    hasExpiredToken() {
        return localStorage.getItem(authTokenKey) !== null && storedToken() === null;
    },

    logout() {
        localStorage.removeItem(authTokenKey);
        localStorage.removeItem(tokenExpiryKey);
        localStorage.removeItem(usernameKey);
        this.session = null;
        delete axios.defaults.headers.common['Authorization'];
    }

};

export default AuthService;
